import { useMemo } from 'react';
import { CPX_FORMATTER } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { LineChart } from '@Components/Charts/LineChart';
import { TableEmptyState } from '@Components/TableEmptyState';
import { LaunchMarkerDot } from './FatigueTimeChart';
import { FOCUS_SERIES, AdSet } from './meta-fatigue-insights-constants';
import { buildFocusChartData, launchesInWindow, shortDate } from './meta-fatigue-insights-utils';

interface FocusChartProps {
  adSet: AdSet;
  dateRange: DateRange;
}

export const FocusChart = ({ adSet, dateRange }: FocusChartProps) => {
  const data = useMemo(() => buildFocusChartData(adSet, dateRange), [adSet, dateRange]);

  const launchesByDate = useMemo(
    () => new Map(launchesInWindow(adSet, dateRange).map(launch => [launch.date, launch])),
    [adSet, dateRange]
  );
  const adNames = useMemo(() => new Map(adSet.ads.map(ad => [ad.adId, ad.name])), [adSet]);

  const lineDataKeys = useMemo(
    () =>
      FOCUS_SERIES.map(series => {
        const yAxisId = series.axisId === 'rate' ? ('left' as const) : ('right' as const);
        if (series.key === 'fatigue' && launchesByDate.size > 0) {
          return {
            dataKey: series.key,
            name: series.label,
            yAxisId,
            dot: <LaunchMarkerDot launchesByDate={launchesByDate} adNames={adNames} />
          };
        }
        if (series.key === 'cpx') {
          // CPX only has a value on days with a conversion, so a dot keeps that day
          // visible even when it can't join a line.
          return { dataKey: series.key, name: series.label, yAxisId, dot: true };
        }
        return { dataKey: series.key, name: series.label, yAxisId };
      }),
    [launchesByDate, adNames]
  );
  const colorMappings = useMemo(
    () => FOCUS_SERIES.map(series => ({ name: series.label, color: series.color })),
    []
  );

  if (data.length === 0) {
    return (
      <TableEmptyState message='No fatigue data for this ad set in the selected date range.' />
    );
  }

  // CPX only plots on days with at least one attributed conversion for the selected KPI —
  // unlike CTR/fatigue, it's derived from the KPI mapping, not raw impressions/clicks, so a
  // set with no conversions in range (or no Meta mapping for the selected KPI) draws no line
  // at all rather than a flat/zero one. Called out explicitly so that reads as "no data",
  // not a broken chart.
  const hasCpx = data.some(point => point.cpx !== null);

  return (
    <>
      <LineChart
        data={data}
        xAxisDataKey='date'
        xAxisTickFormatter={shortDate}
        lineDataKeys={lineDataKeys}
        colorMappings={colorMappings}
        useLeftYAxis
        yAxisTickFormatter={value => value.toFixed(2)} // fatigue and CTR, both decimals
        rightYAxisTickFormatter={CPX_FORMATTER} // just CPX
        showLegend={false}
      />
      {!hasCpx && (
        <p className='bp-focus-note'>
          No conversions attributed to the selected KPI for this ad set in this window — CPX has
          nothing to plot.
        </p>
      )}
    </>
  );
};
